import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { ArrowLeft, Check, Flame, Loader2, Play, SkipForward, Star, Trophy, User, X } from "lucide-react";

import { GET_KID_PROFILE } from "@/graphql/queries/kid";
import { AvatarActions, useAvatarStudio } from "@/components/avatar-studio";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { LetterTeaser, nextLetterSentence } from "@/components/progress-letter";
import { SubjectIcon } from "@/components/subject-icon";
import { useAuth } from "@/lib/auth/AuthContext";
import { ageLabel, exercisesLabel } from "@/lib/students";
import { formatFullDate, formatRelativeDate } from "@/lib/relative-date";

/** Rows shown before "rodyti visus" — enough to see progress, not a wall of it. */
const SKILLS_SHOWN = 8;
const SESSIONS_SHOWN = 10;

/**
 * The learner's own profile: the same page a grown-up sees at /students/:id,
 * with everything they must not touch left out — no edit, no delete, no starting
 * a session for someone else. What is left is theirs: the avatar (and the
 * credits they earned to change it), what they have learned, and what they have
 * already done.
 */
export default function KidProfilePage() {
  const { student: me } = useAuth();
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [showAvatarStyles, setShowAvatarStyles] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // `?avatar=1` is how the earned-avatar notification arrives: it opens the style
  // menu rather than dropping the child on a page and leaving them to find the
  // button. Consumed on arrival — reloading later should not reopen it, and the
  // effect (rather than a mount-time read) is what makes it work when they are
  // already standing on this page.
  useEffect(() => {
    if (searchParams.get("avatar") !== "1") return;

    setShowAvatarStyles(true);
    const next = new URLSearchParams(searchParams);
    next.delete("avatar");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data, loading, refetch } = useQuery(GET_KID_PROFILE, {
    variables: { id: me?.id },
    skip: !me?.id,
    fetchPolicy: "cache-and-network",
  });

  const studio = useAvatarStudio(me?.id, { onChanged: () => refetch() });

  const student = (data as any)?.student;
  const subjects = (data as any)?.subjects ?? [];
  const letterAvailability = (data as any)?.progressReportAvailability;
  const letterWaiting = letterAvailability?.allowed === true;

  if (loading && !student) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Nepavyko atidaryti profilio</p>
        <Button asChild className="mt-4">
          <Link to="/">Į pradžią</Link>
        </Button>
      </div>
    );
  }

  const level = student.level;
  const initials = (student.name ?? "")
    .split(" ")
    .map((part: string) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const subjectIcons: Record<string, string> = Object.fromEntries(
    subjects.map((subject: any) => [subject.id, subject.icon])
  );

  // Only what they have actually practised — an untouched skill is not progress,
  // and a list of every skill in the app would bury the ones that are theirs.
  const practised = (student.proficiencies ?? [])
    .filter((p: any) => p.totalAttempts > 0 || p.proficiency > 0)
    .sort((a: any, b: any) => b.proficiency - a.proficiency);
  const visibleSkills = showAllSkills ? practised : practised.slice(0, SKILLS_SHOWN);

  // A half-built session is a dead end for a learner — the same rule the home
  // page uses to decide what is worth offering.
  const sessions = [...(student.sessions ?? [])]
    .filter((s: any) =>
      s.status === "GENERATED" ? s.exerciseCount > 0 : s.status !== "DRAFT"
    )
    .sort((a: any, b: any) => {
      const aOpen = a.status === "GENERATED";
      const bOpen = b.status === "GENERATED";
      if (aOpen !== bOpen) return aOpen ? -1 : 1;
      const aDate = a.completedAt || a.createdAt;
      const bDate = b.completedAt || b.createdAt;
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });
  const visibleSessions = showAllSessions ? sessions : sessions.slice(0, SESSIONS_SHOWN);

  return (
    <div className="space-y-6">
      {/* Hero — the avatar is the whole point of this page */}
      <div className="relative overflow-hidden rounded-3xl border bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-amber-500/10 dark:from-purple-500/20 dark:via-pink-500/20 dark:to-amber-500/20">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-br from-purple-400/30 to-pink-400/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-br from-amber-400/30 to-orange-400/30 blur-3xl" />

        <div className="relative flex items-center justify-between p-4 sm:p-6">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full bg-background/40 backdrop-blur-sm hover:bg-background/70"
            asChild
          >
            <Link to="/" aria-label="Į pradžią">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="relative flex flex-col items-center gap-6 px-6 pb-10 pt-0 sm:flex-row sm:items-center sm:gap-10 sm:px-10 sm:pb-14">
          <div className="relative flex flex-col items-center gap-3">
            <div className="absolute inset-0 -z-10 scale-110 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-amber-500 opacity-30 blur-2xl" />
            <div className="rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-amber-500 p-1.5 shadow-2xl">
              <Avatar className="h-56 w-56 border-4 border-background sm:h-64 sm:w-64 md:h-72 md:w-72">
                {student.avatarUrl ? (
                  <AvatarImage src={student.avatarUrl} alt={student.name} className="object-cover" />
                ) : null}
                <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-6xl text-white sm:text-7xl">
                  {initials || <User className="h-24 w-24" />}
                </AvatarFallback>
              </Avatar>
            </div>

            <AvatarActions
              studio={studio}
              stylesOpen={showAvatarStyles}
              onStylesOpenChange={setShowAvatarStyles}
            />
          </div>

          <div className="flex flex-1 flex-col items-center text-center sm:items-start sm:text-left">
            {level && (
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border bg-background/70 px-3 py-1 text-sm font-medium shadow-sm backdrop-blur-sm">
                <span className="text-lg leading-none">{level.icon}</span>
                <span>{level.name}</span>
              </div>
            )}
            <h1 className="bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-4xl font-extrabold tracking-tight text-transparent sm:text-5xl md:text-6xl">
              {student.name}
            </h1>
            {(student.age ?? 0) > 0 && (
              <p className="mt-2 text-lg text-muted-foreground">{ageLabel(student.age)}</p>
            )}

            <div className="mt-5 flex flex-wrap justify-center gap-2 sm:justify-start">
              <div className="inline-flex items-center gap-1.5 rounded-full border bg-background/70 px-3 py-1.5 text-sm shadow-sm backdrop-blur-sm">
                <span>💎</span>
                <span className="font-semibold">{student.totalPoints?.toLocaleString()}</span>
                <span className="text-muted-foreground">taškų</span>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border bg-background/70 px-3 py-1.5 text-sm shadow-sm backdrop-blur-sm">
                <Flame className="h-4 w-4 text-orange-500" />
                <span className="font-semibold">{student.currentStreak}</span>
                <span className="text-muted-foreground">dienų serija</span>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border bg-background/70 px-3 py-1.5 text-sm shadow-sm backdrop-blur-sm">
                <Trophy className="h-4 w-4 text-amber-500" />
                <span className="font-semibold">{student.achievements?.length ?? 0}</span>
                <span className="text-muted-foreground">pasiekimų</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* The numbers, one card each */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-yellow-500/10" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lygis</CardTitle>
            <span className="text-2xl">{level?.icon}</span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{level?.name}</div>
            {level?.nextLevel && (
              <div className="mt-2 space-y-1">
                <Progress value={level.progressToNextLevel} className="h-1.5" />
                <p className="text-xs text-muted-foreground">
                  {level.pointsToNextLevel} iki {level.nextLevel.name}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taškai</CardTitle>
            <span className="text-2xl">💎</span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{student.totalPoints?.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Viso surinkta</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-red-500/10" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Serija</CardTitle>
            <Flame className="h-5 w-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{student.currentStreak}</div>
            <p className="text-xs text-muted-foreground">Rekordas: {student.longestStreak} d.</p>
          </CardContent>
        </Card>

        <Link to="/profile/achievements" className="block">
          <Card className="relative overflow-hidden transition-colors hover:border-amber-500/50">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pasiekimai</CardTitle>
              <Trophy className="h-5 w-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{student.achievements?.length ?? 0}</div>
              <p className="text-xs text-muted-foreground">Peržiūrėti visus &rarr;</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* The letter, or the one on its way. Given its own row rather than a fifth
          stat tile: it is the only thing on this page written *to* the child, and a
          tile of it would read as another number. */}
      <Link to="/profile/reports" className="block">
        <Card
          className={`relative overflow-hidden transition-colors hover:border-primary/50 ${
            letterWaiting ? "border-primary/40 shadow-md" : ""
          }`}
        >
          <div
            className={`absolute inset-0 ${
              letterWaiting
                ? "bg-gradient-to-br from-emerald-500/15 via-amber-500/10 to-pink-500/15"
                : "bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10"
            }`}
          />
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{letterWaiting ? "💌" : "✉️"}</span>
              <div>
                <CardTitle>
                  {letterWaiting ? "Tau atėjo naujas laiškas!" : "Kaip tau sekasi"}
                </CardTitle>
                {/* When a letter is waiting, say so. When it is not, say when the
                    next one comes — in the same words the letters page uses, so a
                    child is not told about two different letters. */}
                <CardDescription>
                  {letterWaiting
                    ? "Atidaryk ir pažiūrėk, kuo gali didžiuotis"
                    : letterAvailability
                      ? nextLetterSentence(letterAvailability)
                      : "Laiškai apie tai, ką jau moki"}
                </CardDescription>
              </div>
            </div>
            <LetterTeaser availability={letterAvailability} />
          </CardHeader>
        </Card>
      </Link>

      {/* What they have learned */}
      <Card>
        <CardHeader>
          <CardTitle>Ką jau moki</CardTitle>
          <CardDescription>Įgūdžiai, kuriuos jau bandei</CardDescription>
        </CardHeader>
        <CardContent>
          {practised.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Kol kas tuščia — išspręsk pirmą sesiją ir čia atsiras tavo įgūdžiai.
            </p>
          ) : (
            <div className="space-y-4">
              {visibleSkills.map((prof: any) => (
                <div key={prof.id} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <SubjectIcon
                      icon={subjectIcons[prof.skill?.subjectId ?? ""]}
                      className="h-4 w-4 shrink-0 text-muted-foreground"
                    />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {prof.skill?.name ?? prof.skillKey}
                    </span>
                    <span className="shrink-0 text-sm font-semibold text-muted-foreground">
                      {Math.round(prof.proficiency)}%
                    </span>
                  </div>
                  <Progress value={prof.proficiency} className="h-2" />
                  {prof.totalAttempts > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {prof.correctAttempts} iš {prof.totalAttempts} teisingai
                    </p>
                  )}
                </div>
              ))}

              {practised.length > SKILLS_SHOWN && (
                <button
                  onClick={() => setShowAllSkills((v) => !v)}
                  className="text-sm text-muted-foreground underline-offset-4 hover:underline"
                >
                  {showAllSkills
                    ? "Rodyti mažiau"
                    : `Rodyti visus (${practised.length})`}
                </button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* What they have done */}
      <Card>
        <CardHeader>
          <CardTitle>Tavo sesijos</CardTitle>
          <CardDescription>Ką jau išsprendei ir kas dar nebaigta</CardDescription>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Dar nėra nė vienos sesijos.</p>
          ) : (
            <div className="space-y-3">
              {visibleSessions.map((session: any) => {
                const displayDate = session.completedAt || session.createdAt;
                const unfinished = session.status === "GENERATED";
                return (
                  <div
                    key={session.id}
                    className="flex items-center justify-between gap-3 rounded-lg border p-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <SubjectIcon
                        icon={session.subject?.icon}
                        className="h-6 w-6 shrink-0 text-primary"
                      />
                      <div className="flex min-w-0 flex-col gap-1">
                        <span className="truncate text-sm font-medium">
                          {session.subject?.name ?? session.subjectKey}
                        </span>
                        <span
                          className="text-xs text-muted-foreground"
                          title={formatFullDate(displayDate)}
                        >
                          {formatRelativeDate(displayDate)}
                        </span>
                        {unfinished ? (
                          <span className="text-xs text-muted-foreground">
                            {exercisesLabel(session.exerciseCount)}
                          </span>
                        ) : (
                          session.totalCount > 0 && (
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {session.pointsEarned > 0 && (
                              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                <Star className="h-3 w-3" />
                                {session.pointsEarned}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                              <Check className="h-3 w-3" />
                              {session.correctCount}
                            </span>
                            {session.incorrectCount > 0 && (
                              <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                                <X className="h-3 w-3" />
                                {session.incorrectCount}
                              </span>
                            )}
                            {session.skippedCount > 0 && (
                              <span className="inline-flex items-center gap-1 text-gray-500">
                                <SkipForward className="h-3 w-3" />
                                {session.skippedCount}
                              </span>
                            )}
                            </div>
                          )
                        )}
                      </div>
                    </div>

                    {/* An unfinished session is the one thing here worth tapping. */}
                    {unfinished ? (
                      <Button size="sm" asChild>
                        <Link to={`/sessions/${session.id}/solve`}>
                          <Play className="mr-1.5 h-3.5 w-3.5" />
                          Tęsk
                        </Link>
                      </Button>
                    ) : (
                      <Badge variant="secondary">
                        {session.status === "GRADED" ? "Įvertinta" : "Baigta"}
                      </Badge>
                    )}
                  </div>
                );
              })}

              {sessions.length > SESSIONS_SHOWN && (
                <button
                  onClick={() => setShowAllSessions((v) => !v)}
                  className="text-sm text-muted-foreground underline-offset-4 hover:underline"
                >
                  {showAllSessions ? "Rodyti mažiau" : `Rodyti visas (${sessions.length})`}
                </button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
